import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import {
    Box,
    IconButton,
    useAppDispatch,
    useAppSelector,
    useTheme,
    FormInputText,
    FormTextEditor,
    FormChipInput,
} from '../../../../base';
import {
    ISendEmailForm,
    SendEmailFormFields,
    IJobApplication,
    emptySendEmailForm,
    borderlessInputSx,
    isValidEmail,
} from '../../../../common';
import { sendEmailSchema } from '../../../../common/validations/jobApplications.validations';
import {
    emailTemplatesActions,
    emailTemplatesSelectors,
    userSelectors,
} from '../../../../actions';
import TemplatePicker from './TemplatePicker';
import {
    FieldRow,
    FieldLabel,
    FieldContent,
    CcBccToggle,
    EditorWrapper,
    FooterRow,
    SendButton,
} from './SendEmailBox.styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface SendEmailBoxProps {
    jobApplication: IJobApplication;
    onSave?: () => void;
}

const SendEmailBox = ({ jobApplication, onSave }: SendEmailBoxProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const editorWrapperRef = useRef<HTMLDivElement>(null);
    const [toolbarPortal, setToolbarPortal] = useState<HTMLElement | null>(
        null
    );

    const dispatch = useAppDispatch();
    const myUser = useAppSelector(userSelectors.getMyUser);
    const pickerOptions = useAppSelector(
        emailTemplatesSelectors.getPickerOptions
    );
    const isLoadingPickerOptions = useAppSelector(
        emailTemplatesSelectors.getIsLoadingPickerOptions
    );
    const isApplyingTemplate = useAppSelector(
        emailTemplatesSelectors.getIsApplyingTemplate
    );
    const renderedTemplate = useAppSelector(
        emailTemplatesSelectors.getRenderedTemplate
    );

    const methods = useForm<ISendEmailForm>({
        resolver: yupResolver(sendEmailSchema(t)) as any,
        mode: 'onSubmit',
        defaultValues: {
            ...emptySendEmailForm,
            [SendEmailFormFields.to]: jobApplication.email,
            [SendEmailFormFields.from]: myUser?.email || '',
        },
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = methods;

    useEffect(() => {
        reset({
            ...emptySendEmailForm,
            [SendEmailFormFields.to]: jobApplication.email,
            [SendEmailFormFields.from]: myUser?.email || '',
        });
        setShowCc(false);
        setShowBcc(false);
        setAttachments([]);
    }, [reset, jobApplication.email]);

    useEffect(() => {
        dispatch(emailTemplatesActions.fetchPickerOptions());
        return () => {
            dispatch(emailTemplatesActions.clearPicker());
        };
    }, [dispatch]);

    useEffect(() => {
        const toolbar =
            editorWrapperRef.current?.querySelector('.ql-toolbar');
        if (!toolbar) return;
        const host = document.createElement('div');
        host.style.marginInlineStart = 'auto';
        toolbar.appendChild(host);
        setToolbarPortal(host);
        return () => {
            host.remove();
            setToolbarPortal(null);
        };
    }, []);

    useEffect(() => {
        if (!renderedTemplate) return;
        setValue(SendEmailFormFields.subject, renderedTemplate.subject, {
            shouldValidate: true,
        });
        setValue(
            SendEmailFormFields.body,
            DOMPurify.sanitize(renderedTemplate.body),
            { shouldValidate: true }
        );
        dispatch(emailTemplatesActions.clearPicker());
    }, [renderedTemplate, setValue, dispatch]);

    const handleApplyTemplate = (templateId: number) => {
        dispatch(
            emailTemplatesActions.applyTemplate({
                jobApplicationId: jobApplication.id,
                emailTemplateId: templateId,
            })
        );
    };

    const onSubmit = (data: ISendEmailForm) => {
        // TODO: Integrate with backend API
        console.log('Send email:', { ...data, attachments });
        onSave?.();
    };

    const handleClearAndClose = () => {
        reset({
            ...emptySendEmailForm,
            [SendEmailFormFields.to]: jobApplication.email,
            [SendEmailFormFields.from]: myUser?.email || '',
        });
        setShowCc(false);
        setShowBcc(false);
        setAttachments([]);
        onSave?.();
    };

    const handleFilesAttached = (files: File[]) => {
        setAttachments((prev) => [...prev, ...files]);
    };

    const handleRemoveAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <FieldRow>
                <FieldLabel>{t('jobApplicationPage.to')}</FieldLabel>
                <FieldContent>
                    <FormInputText
                        name={SendEmailFormFields.to}
                        control={control}
                        disabled
                        size="small"
                        removeBottomPadding
                        sx={borderlessInputSx}
                    />
                </FieldContent>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <CcBccToggle
                        onClick={() => setShowCc(!showCc)}
                        sx={{
                            color: showCc
                                ? theme.palette.primary.main
                                : theme.palette.grey[700],
                        }}
                    >
                        {t('jobApplicationPage.cc')}
                    </CcBccToggle>
                    <CcBccToggle
                        onClick={() => setShowBcc(!showBcc)}
                        sx={{
                            color: showBcc
                                ? theme.palette.primary.main
                                : theme.palette.grey[700],
                        }}
                    >
                        {t('jobApplicationPage.bcc')}
                    </CcBccToggle>
                </Box>
            </FieldRow>

            {showCc && (
                <FieldRow>
                    <FieldLabel>{t('jobApplicationPage.cc')}</FieldLabel>
                    <FieldContent>
                        <FormChipInput
                            name={SendEmailFormFields.cc}
                            control={control}
                            validate={isValidEmail}
                            placeholder={t('jobApplicationPage.addEmail')}
                        />
                    </FieldContent>
                </FieldRow>
            )}

            {showBcc && (
                <FieldRow>
                    <FieldLabel>{t('jobApplicationPage.bcc')}</FieldLabel>
                    <FieldContent>
                        <FormChipInput
                            name={SendEmailFormFields.bcc}
                            control={control}
                            validate={isValidEmail}
                            placeholder={t('jobApplicationPage.addEmail')}
                        />
                    </FieldContent>
                </FieldRow>
            )}

            <FieldRow>
                <FieldLabel>{t('jobApplicationPage.from')}</FieldLabel>
                <FieldContent>
                    <FormInputText
                        name={SendEmailFormFields.from}
                        control={control}
                        disabled
                        size="small"
                        removeBottomPadding
                        sx={borderlessInputSx}
                    />
                </FieldContent>
            </FieldRow>

            <FieldRow>
                <FieldLabel
                    sx={{
                        color: errors[SendEmailFormFields.subject]
                            ? 'error.main'
                            : undefined,
                    }}
                >
                    {t('jobApplicationPage.subject')}
                </FieldLabel>
                <FieldContent>
                    <FormInputText
                        name={SendEmailFormFields.subject}
                        control={control}
                        size="small"
                        removeBottomPadding
                        sx={borderlessInputSx}
                    />
                </FieldContent>
            </FieldRow>

            <EditorWrapper ref={editorWrapperRef}>
                <FormTextEditor
                    name={SendEmailFormFields.body}
                    control={control}
                    attachments={attachments}
                    onFilesAttached={handleFilesAttached}
                    onRemoveAttachment={handleRemoveAttachment}
                />
            </EditorWrapper>

            {toolbarPortal &&
                createPortal(
                    <TemplatePicker
                        options={pickerOptions}
                        isLoading={isLoadingPickerOptions}
                        isApplying={isApplyingTemplate}
                        onApply={handleApplyTemplate}
                    />,
                    toolbarPortal
                )}

            <FooterRow>
                <IconButton onClick={handleClearAndClose}>
                    <DeleteOutlineIcon />
                </IconButton>
                <SendButton
                    textColor={theme.palette.common.white}
                    variant="contained"
                    type="submit"
                    loadingPosition="start"
                    startIcon={<></>}
                    loading={false}
                >
                    {t('jobApplicationPage.send')}
                </SendButton>
            </FooterRow>
        </form>
    );
};

export default SendEmailBox;
